/*
 * charts.jsx
 *
 * Recharts-based chart components. All accept pre-fetched data as props —
 * no internal data fetching. Parent (DetailPanel) owns the fetch state.
 * Exports: HealthTooltip, HealthChart, UsageTooltip, UsageXTick, UsageChart,
 *          fmtHourLabel, UsageHourTick, UsageTooltip24h, UsageChart24h,
 *          TELEMETRY_COLORS, prettyMetric, TelemetryTooltip, TelemetryChart.
 * Imported by: DetailPanel.jsx.
 */

import { BarChart, Bar, XAxis, YAxis, ReferenceLine, Tooltip, ResponsiveContainer, ComposedChart, Line, Legend } from "recharts";

// ── Health chart (baseline deviation) ────────────────────────────────────────

export const HealthTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const fmt = (v) => typeof v === "number" ? (v < 0.01 ? v.toFixed(5) : v < 1 ? v.toFixed(3) : v.toFixed(2)) : v;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px", fontSize: 11, lineHeight: 1.7, maxWidth: 260 }}>
      <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>{d.name}</div>
      <div style={{ color: "#EF4444" }}>{d.pct}% of threshold · {d.sigmas}σ above baseline</div>
      <div style={{ borderTop: "1px solid #F3F4F6", marginTop: 6, paddingTop: 6 }}>
        <div><span style={{ color: "#9CA3AF" }}>Current value: </span><strong>{fmt(d.current)}</strong></div>
        <div><span style={{ color: "#9CA3AF" }}>Healthy avg: </span>{fmt(d.normal)}</div>
        <div><span style={{ color: "#9CA3AF" }}>Breach threshold: </span>{fmt(d.threshold)}</div>
      </div>
      {d.threshold < 0.05 && (
        <div style={{ marginTop: 6, fontSize: 10, color: "#D97706", borderTop: "1px solid #FEF3C7", paddingTop: 6 }}>
          ⚠ Near-zero baseline — threshold is {fmt(d.threshold)}. High % may reflect a tiny absolute change.
        </div>
      )}
    </div>
  );
};

export const HealthChart = ({ deviation, expanded }) => {
  if (!deviation) {
    return <div style={{ padding: 16, fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>Baseline not available for this charger</div>;
  }
  if (!deviation.breaches || deviation.breaches.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: "#0EA5E9", textAlign: "center" }}>No baseline breaches — charger within normal range</div>;
  }
  const data = deviation.breaches.map(b => {
    const pct = Math.round((b.current / b.threshold) * 100);
    return {
      name: b.feature.replace(/_/g, " ").replace("24hr", "24h").replace("6hr", "6h"),
      pct,
      sigmas: b.sigmas,
      current:   b.current,
      normal:    b.normal,
      threshold: b.threshold,
      fill: pct > 150 ? "#EF4444" : pct > 100 ? "#F97316" : "#0EA5E9",
    };
  });
  const allNearZero = data.every(d => d.threshold < 0.05);
  return (
    <>
      {allNearZero && (
        <div className="dp-card dp-card-amber" style={{ fontSize: 10, color: "#D97706", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4, padding: "5px 10px", marginBottom: 8 }}>
          ⚠ All breach thresholds are near-zero (baseline built from a charger with very low historical fault rates).
          High percentages here may reflect tiny absolute values — hover each bar to see actual numbers.
        </div>
      )}
      <ResponsiveContainer width="100%" height={expanded ? 360 : 180}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: -16, bottom: expanded ? 64 : 48 }}>
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#9CA3AF" }} angle={-35} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 9, fill: "#9CA3AF" }} unit="%" />
          <Tooltip content={<HealthTooltip />} />
          <ReferenceLine y={100} stroke="#1E3A8A" strokeDasharray="4 2"
            label={{ value: "threshold", fill: "#1E3A8A", fontSize: 9, position: "insideTopRight" }} />
          <Bar dataKey="pct" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

// ── Usage chart (30-day sessions + faults) ────────────────────────────────────

const IMPACT_COLOR = { H: "#FCA5A5", M: "#FDBA74", L: "#FDE68A", N: "#E5E7EB" };
const IMPACT_LABEL = { H: "H — High", M: "M — Medium", L: "L — Low", N: "N — Info" };

export const UsageTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  const totalFaults = (d.faults_H || 0) + (d.faults_M || 0) + (d.faults_L || 0) + (d.faults_N || 0);

  const FaultSection = ({ level, count, errors }) => {
    if (!count) return null;
    const top3  = errors.slice(0, 3);
    const extra = errors.length - 3;
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ color: level === 'H' ? '#DC2626' : level === 'M' ? '#EA580C' : level === 'L' ? '#B45309' : '#6B7280', fontWeight: 700, fontSize: 10 }}>[{level}] {level === 'H' ? 'High' : level === 'M' ? 'Medium' : level === 'L' ? 'Low' : 'Info'} faults:</div>
        {top3.map((e, i) => (
          <div key={i} style={{ color: "#374151", paddingLeft: 8 }}>
            {e.count} — {e.description} ({e.code})
          </div>
        ))}
        {extra > 0 && <div style={{ color: "#9CA3AF", paddingLeft: 8 }}>... and {extra} more</div>}
      </div>
    );
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px", fontSize: 11, lineHeight: 1.6, maxWidth: 300 }}>
      <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>{d.date}</div>
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 4, display: "flex", gap: 16 }}>
        <span style={{ color: "#1E3A8A" }}>Successful: {d.successful ?? 0}</span>
        <span style={{ color: "#EF4444" }}>Failed: {d.failed ?? 0}</span>
        {d.power_loss > 0 && <span style={{ color: "#D97706" }}>⚡ {d.power_loss}</span>}
      </div>
      {(d.ghost_sessions || 0) > 0 && (
        <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 4, marginTop: 4, color: "#DC2626" }}>
          Ghost sessions: {d.ghost_sessions} — sessions started but never formally ended
        </div>
      )}
      {totalFaults > 0 && (
        <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 4, marginTop: 4 }}>
          <div style={{ fontWeight: 700, color: "#374151" }}>FAULTS: {totalFaults}</div>
          <FaultSection level="H" count={d.faults_H} errors={d.errors_H || []} />
          <FaultSection level="M" count={d.faults_M} errors={d.errors_M || []} />
          <FaultSection level="L" count={d.faults_L} errors={d.errors_L || []} />
          <FaultSection level="N" count={d.faults_N} errors={d.errors_N || []} />
        </div>
      )}
    </div>
  );
};

export const UsageXTick = ({ x, y, payload, data }) => {
  const entry = data?.find(d => d.date === payload.value);
  const hasH  = (entry?.faults_H || 0) > 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="end" fill="#9CA3AF" fontSize={9} transform="rotate(-35)">
        {payload.value?.slice(5)}
      </text>
      {hasH && (
        <text x={0} y={18} textAnchor="middle" fill="#EF4444" fontSize={9}>⚠</text>
      )}
    </g>
  );
};

export const UsageChart = ({ data, height = 200, expanded = false }) => {
  const margin = expanded
    ? { top: 8, right: 52, left: 4, bottom: 48 }
    : { top: 4, right: 44, left: -16, bottom: 40 };
  const fs = expanded ? 10 : 9;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={margin}>
        <XAxis dataKey="date" tick={<UsageXTick data={data} />} interval={0} />
        <YAxis yAxisId="left" tick={{ fontSize: fs, fill: "#374151" }} allowDecimals={false}
          label={{ value: "Sessions", angle: -90, position: "insideLeft", fontSize: fs, fill: "#374151", dx: expanded ? 14 : 18 }} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: fs, fill: "#F97316" }} allowDecimals={false}
          label={{ value: "Faults", angle: 90, position: "insideRight", fontSize: fs, fill: "#F97316", dx: expanded ? -4 : -2 }} />
        <Tooltip content={<UsageTooltip />} />
        <Legend wrapperStyle={{ fontSize: fs, paddingTop: 6 }} />
        <Bar yAxisId="right" dataKey="faults_H" stackId="faults" fill={IMPACT_COLOR.H} name={IMPACT_LABEL.H} />
        <Bar yAxisId="right" dataKey="faults_M" stackId="faults" fill={IMPACT_COLOR.M} name={IMPACT_LABEL.M} />
        <Bar yAxisId="right" dataKey="faults_L" stackId="faults" fill={IMPACT_COLOR.L} name={IMPACT_LABEL.L} />
        <Bar yAxisId="right" dataKey="faults_N" stackId="faults" fill={IMPACT_COLOR.N} name={IMPACT_LABEL.N} radius={[2, 2, 0, 0]} />
        <Line yAxisId="left" type="monotone" dataKey="successful" stroke="#1E3A8A" strokeWidth={2} dot={false} name="Successful Sessions" />
        <Line yAxisId="left" type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} dot={false} name="Failed Sessions" />
        {data.some(d => (d.ghost_sessions || 0) > 0) && (
          <Line yAxisId="left" type="monotone" dataKey="ghost_sessions" stroke="#DC2626" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Ghost Sessions" />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ── 24-hour usage chart ───────────────────────────────────────────────────────

export const fmtHourLabel = (h) => {
  const dt = new Date(h);
  return isNaN(dt.getTime()) ? h : dt.toLocaleTimeString("en-US", { hour: "numeric", hour12: true });
};

export const UsageHourTick = ({ x, y, payload, data }) => {
  const entry = data?.find(d => d.hour === payload.value);
  const hasH  = (entry?.faults_H || 0) > 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={10} textAnchor="end" fill={hasH ? "#EF4444" : "#9CA3AF"} fontSize={9} transform="rotate(-35)">
        {fmtHourLabel(payload.value)}
      </text>
      {hasH && <text x={0} y={18} textAnchor="middle" fill="#EF4444" fontSize={9}>⚠</text>}
    </g>
  );
};

export const UsageTooltip24h = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  const totalFaults = (d.faults_H || 0) + (d.faults_M || 0) + (d.faults_L || 0) + (d.faults_N || 0);
  const fmtTs = (h) => {
    const dt = new Date(h);
    return isNaN(dt.getTime()) ? h : dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  };
  const FaultSection24h = ({ level, count, errors }) => {
    if (!count) return null;
    const col = { H: '#DC2626', M: '#EA580C', L: '#B45309', N: '#6B7280' }[level];
    const lbl = { H: 'High', M: 'Medium', L: 'Low', N: 'Info' }[level];
    return (
      <div style={{ marginTop: 5 }}>
        <div style={{ color: col, fontWeight: 700, fontSize: 10 }}>[{level}] {lbl} — {count} fault{count !== 1 ? "s" : ""}</div>
        {errors.map((e, i) => (
          <div key={i} style={{ paddingLeft: 8, marginTop: 2 }}>
            <div style={{ color: "#111827", fontWeight: 600 }}>
              #{e.code}{e.is_precursor && <span style={{ color: "#7C3AED", fontSize: 9, marginLeft: 4, fontWeight: 700 }}>PRECURSOR</span>}
              <span style={{ color: "#9CA3AF", fontWeight: 400, marginLeft: 6 }}>×{e.count}</span>
            </div>
            <div style={{ color: "#374151", fontSize: 10 }}>{e.description}</div>
            {(e.category || e.subcategory) && (
              <div style={{ color: "#9CA3AF", fontSize: 9 }}>{[e.category, e.subcategory].filter(Boolean).join(" › ")}</div>
            )}
          </div>
        ))}
      </div>
    );
  };
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 12px", fontSize: 11, lineHeight: 1.6, maxWidth: 320 }}>
      <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>{fmtTs(d.hour)}</div>
      <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 4, display: "flex", gap: 14 }}>
        <span style={{ color: "#1E3A8A" }}>✓ {d.successful ?? 0}</span>
        <span style={{ color: "#EF4444" }}>✗ {d.failed ?? 0}</span>
        {(d.power_loss || 0) > 0 && <span style={{ color: "#D97706" }}>⚡ {d.power_loss} power loss</span>}
        {(d.ghost_sessions || 0) > 0 && <span style={{ color: "#DC2626" }}>👻 {d.ghost_sessions} ghost</span>}
      </div>
      {totalFaults > 0 && (
        <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 4, marginTop: 4 }}>
          <div style={{ fontWeight: 700, color: "#374151", fontSize: 11 }}>FAULTS: {totalFaults}</div>
          <FaultSection24h level="H" count={d.faults_H} errors={d.errors_H || []} />
          <FaultSection24h level="M" count={d.faults_M} errors={d.errors_M || []} />
          <FaultSection24h level="L" count={d.faults_L} errors={d.errors_L || []} />
          <FaultSection24h level="N" count={d.faults_N} errors={d.errors_N || []} />
        </div>
      )}
    </div>
  );
};

export const UsageChart24h = ({ data, height = 200, expanded = false }) => {
  const margin = expanded
    ? { top: 8, right: 52, left: 4, bottom: 48 }
    : { top: 4, right: 44, left: -16, bottom: 40 };
  const fs = expanded ? 10 : 9;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={margin}>
        <XAxis dataKey="hour" tick={<UsageHourTick data={data} />} interval={0} />
        <YAxis yAxisId="left" tick={{ fontSize: fs, fill: "#374151" }} allowDecimals={false}
          label={{ value: "Sessions", angle: -90, position: "insideLeft", fontSize: fs, fill: "#374151", dx: expanded ? 14 : 18 }} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: fs, fill: "#F97316" }} allowDecimals={false}
          label={{ value: "Faults", angle: 90, position: "insideRight", fontSize: fs, fill: "#F97316", dx: expanded ? -4 : -2 }} />
        <Tooltip content={<UsageTooltip24h />} />
        <Legend wrapperStyle={{ fontSize: fs, paddingTop: 6 }} />
        <Bar yAxisId="right" dataKey="faults_H" stackId="faults" fill={IMPACT_COLOR.H} name={IMPACT_LABEL.H} />
        <Bar yAxisId="right" dataKey="faults_M" stackId="faults" fill={IMPACT_COLOR.M} name={IMPACT_LABEL.M} />
        <Bar yAxisId="right" dataKey="faults_L" stackId="faults" fill={IMPACT_COLOR.L} name={IMPACT_LABEL.L} />
        <Bar yAxisId="right" dataKey="faults_N" stackId="faults" fill={IMPACT_COLOR.N} name={IMPACT_LABEL.N} radius={[2,2,0,0]} />
        <Line yAxisId="left" type="monotone" dataKey="successful" stroke="#1E3A8A" strokeWidth={2} dot={false} name="Successful Sessions" />
        <Line yAxisId="left" type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} dot={false} name="Failed Sessions" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ── Telemetry chart (OCPP MeterValues time-series) ────────────────────────────

export const TELEMETRY_COLORS = [
  "#2563EB", "#F97316", "#16A34A", "#DC2626", "#9333EA",
  "#0891B2", "#CA8A04", "#DB2777", "#475569", "#65A30D",
];

export const prettyMetric = (key) => String(key || "").replace(/[._]/g, " ").trim();

export const TelemetryTooltip = ({ active, payload, label, unitByKey, labelFmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 6, padding: "8px 11px", fontSize: 11, lineHeight: 1.6, maxWidth: 280 }}>
      <div style={{ fontWeight: 700, color: "#111827", marginBottom: 4 }}>{labelFmt ? labelFmt(label) : label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: "flex", justifyContent: "space-between", gap: 14 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>
            {p.value == null ? "—" : Number(p.value).toLocaleString("en-US", { maximumFractionDigits: 2 })}
            {unitByKey[p.dataKey] ? ` ${unitByKey[p.dataKey]}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
};

export const TelemetryChart = ({ series, metrics, rangeMode, height = 220, expanded = false }) => {
  const units = [];
  metrics.forEach(m => { const u = m.unit || "value"; if (!units.includes(u)) units.push(u); });
  const unitByKey = {};
  metrics.forEach(m => { unitByKey[m.key] = m.unit; });

  const fmtX = (t) => {
    const d = new Date(t);
    if (isNaN(d.getTime())) return t;
    return rangeMode === "24h"
      ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
  };
  const fmtLabel = (t) => {
    const d = new Date(t);
    if (isNaN(d.getTime())) return t;
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const fs = expanded ? 11 : 9;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={series} margin={{ top: 8, right: units.length > 1 ? 8 : 16, left: -6, bottom: expanded ? 24 : 16 }}>
        <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize: fs, fill: "#9CA3AF" }} minTickGap={expanded ? 44 : 26} />
        {units.map((u, i) => (
          <YAxis
            key={u} yAxisId={u} orientation={i === 0 ? "left" : "right"} hide={i > 1}
            tick={{ fontSize: fs, fill: "#9CA3AF" }} width={i > 1 ? 0 : 46}
            label={i < 2 ? { value: u, angle: i === 0 ? -90 : 90, position: i === 0 ? "insideLeft" : "insideRight", fontSize: fs, fill: "#9CA3AF" } : undefined}
          />
        ))}
        <Tooltip content={<TelemetryTooltip unitByKey={unitByKey} labelFmt={fmtLabel} />} />
        <Legend wrapperStyle={{ fontSize: fs, paddingTop: 4 }} />
        {metrics.map((m, i) => (
          <Line
            key={m.key} yAxisId={m.unit || "value"} type="monotone" dataKey={m.key}
            name={m.label || prettyMetric(m.key)}
            stroke={m.color || TELEMETRY_COLORS[i % TELEMETRY_COLORS.length]}
            strokeWidth={1.8} dot={false} connectNulls isAnimationActive={false}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
